/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { artifactService } from '../services/artifact.service';
import type { ArtifactTypeName } from '../types/artifact-usage';

/**
 * **Which artifact's inheritance, and the cache key its own reads live under** (`P3a`, slice 7a).
 *
 * `queryRoot` is required because it is the correctness property, not a convenience: the inheritance
 * answer is keyed *under* the artifact's own key, so every invalidation a save already makes refreshes
 * it. An edit changes which leaves the child states, so an answer that outlived a save would mark a
 * field `inherited` while the operator was looking at the value they had just typed into it — the
 * stale-claim shape (#999).
 */
export interface ArtifactInheritanceTarget {
  type: ArtifactTypeName;
  queryRoot: string;
}

/**
 * **Which of this artifact's values are its own** (`P3a`), for any type.
 *
 * `enabled` on the id only: an artifact with no parent is a perfectly good answer (`parentId: null`),
 * not a reason to skip the query, and the projection renders nothing for it.
 */
export function useArtifactInheritance(target: ArtifactInheritanceTarget, id: string | number) {
  return useQuery({
    queryKey: [target.queryRoot, id, 'inheritance'],
    queryFn: () => artifactService.inheritance(target.type, id),
    enabled: !!id,
  });
}

/**
 * Give values back to the parent (`P3a`), for any type.
 *
 * Invalidates the artifact rather than patching the cache: what the operator must now see is the
 * **parent's** value, and the console does not have it — the whole point of sending paths instead of
 * values. Reading it back is the only honest way to show what the revert produced.
 */
export function useRevertArtifactValues(target: ArtifactInheritanceTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paths }: { id: string | number; paths: string[] }) =>
      artifactService.revert(target.type, id, paths),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [target.queryRoot] }),
  });
}
